import { useQuery } from '@tanstack/react-query'
import services from '../../../services'
import { Payment } from '../../../@types/payment'
import { useCallback } from 'react'

interface Last3MonthsPayments {
  date: string
  data: Payment[]
}

interface UseDataProps {
  startDate?: Date
  endDate?: Date
}

export function useData({ endDate, startDate }: UseDataProps) {
  const queryFn = async () => {
    if (startDate || endDate)
      return await services.payments.getAllByDates(
        startDate?.toISOString() ?? '',
        endDate?.toISOString() ?? '',
      )

    return await services.payments.getAll()
  }

  const {
    data: payments,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryFn,
    queryKey: ['/payments/logs', endDate, startDate],
  })

  const getLas3MonthsPayments = useCallback((data: typeof payments) => {
    if (!data) return []

    const currentDate = new Date()
    const currentMonth = currentDate.getMonth()
    const currentYear = currentDate.getFullYear()
    const threeMonthsAgo = new Date(
      currentYear,
      currentMonth - 2,
      1,
    ).toISOString()

    const last3MonthsRows = data
      .filter(({ createdAt }) => createdAt >= threeMonthsAgo)
      .sort(
        (a, b) =>
          new Date(a.createdAt).getMonth() - new Date(b.createdAt).getMonth(),
      )

    const last3MonthsOfYear: string[] = Array.from<string>(
      new Set(
        last3MonthsRows.map(({ createdAt }) => {
          const paymentDate = new Date(createdAt)
          return `${paymentDate.getUTCFullYear()}/${paymentDate.getUTCMonth()}`
        }),
      ),
    )

    return last3MonthsOfYear.map<Last3MonthsPayments>((yearAndMonth) => {
      const date = new Date(yearAndMonth)
      return {
        date: date.toISOString(),
        data: data.filter((item) => {
          return (
            new Date(item.createdAt).getUTCMonth() === date.getUTCMonth() &&
            item.createdAt <= currentDate.toISOString()
          )
        }),
      }
    })
  }, [])

  const getPaymentById = (id: string) => {
    const payment: Payment | null =
      payments?.find((item) => item.id === id) ?? null

    return payment
  }

  return {
    isLoading,
    error,
    payments,
    refetch,
    getPaymentById,
    getLas3MonthsPayments,
  }
}
